import React, { useState, useEffect, useRef } from 'react';
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import Avatar from "../avatar";
import BookingPanel from "../bookingPanel";

import ClientsService from "../../services/clientsService";
import UsersService from "../../services/usersService";
import PatientsService from "../../services/patientsService";
import ClientLocationsService from "../../services/clientLocationsService";
import { getUserName, scrollToTop, getWithLeadingZero, getOpeningHoursByDayIndex, getDiffInDays } from "./../../utils";


import moment from 'moment';
import CalendarsService from '../../services/calendarsService';
import LoggingService from '../../services/loggingService';
import { LogItemType } from '../../models/logItem';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { t } from 'i18next';

// Interface for custom color configuration
interface CustomColorConfig {
    useCustomColors: boolean;
    primaryColor: string;
    secondaryColor: string;
    id?: string; // Optional ID for debugging
}

export default function DoctorPage() {

    const [isLoading, setIsLoading] = useState(true);

    const [locationLogoUrl, setLocationLogoUrl] = useState("");
    const [client, setClient] = useState<any>({ publicTransports: [], accessInformation: [], openingHours: [] });
    const [location, setLocation] = useState<any>(null);
    const [doctor, setDoctor] = useState<any>(null);
    const [doctors, setDoctors] = useState<any>(null);
    const [calendars, setCalendars] = useState([]);

    const [showOnlyTodayOpeningHours, setShowOnlyTodayOpeningHours] = useState(true);

    const [authUser, setAuthUser] = useState<any>(null);
    const [patient, setPatient] = useState<any>(null);
    
    // Flag to track if we're in the middle of a navigation
    const [isNavigating, setIsNavigating] = useState(false);
    
    // Ref to track if component is mounted
    const isMountedRef = useRef(true);

    let { clientId, locationId, userId } = useParams();

    /**
     * Validates if a string is a valid hex color
     * @param color - The color string to validate
     * @returns true if the color is a valid hex color, false otherwise
     */
    const isValidHexColor = (color: string): boolean => {
        return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
    };

    /**
     * Applies custom colors from the location data to override default CSS colors
     * @param locationData - The location data containing custom color configuration
     */
    const applyCustomColors = (locationData: CustomColorConfig) => {
        if (locationData?.useCustomColors && 
            locationData?.primaryColor && 
            locationData?.secondaryColor &&
            isValidHexColor(locationData.primaryColor) &&
            isValidHexColor(locationData.secondaryColor)) {
            // Create CSS custom properties for custom colors
            const root = document.documentElement;

            // primary is stored as secondary and vice versa
            // ToDo: we need to fix this later
            const primaryColor = locationData.secondaryColor;
            const secondaryColor = locationData.primaryColor;


            // Set primary colors
            root.style.setProperty('--kt-primary-color', primaryColor);
            root.style.setProperty('--kt-primary-color-light', primaryColor);
            
            // Set secondary colors
            root.style.setProperty('--kt-secondary-color', secondaryColor);
            root.style.setProperty('--kt-secondary-color-light', secondaryColor);
            
            // Add CSS rules to override default colors
            const style = document.createElement('style');
            style.setAttribute('data-custom-colors', 'true');
            style.textContent = `
                .kt-doctorPage {
                    --kt-primary-color: ${primaryColor};
                    --kt-primary-color-light: ${primaryColor};
                    --kt-secondary-color: ${secondaryColor};
                    --kt-secondary-color-light: ${secondaryColor};
                }
                
                /* Smooth transitions for color changes */
                .kt-doctorPage * {
                    transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
                }
                
                /* More specific selectors to ensure custom colors override base CSS */
                .kt-doctorPage .kt-body .kt-panel.kt-with-icon .kt-panel-icon {
                    background: linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor} 100%);
                }
                
                .kt-doctorPage .kt-body .kt-panel h1,
                .kt-doctorPage .kt-body .kt-panel h2,
                .kt-doctorPage .kt-body .kt-panel h3,
                .kt-doctorPage .kt-body .kt-avatar-columns .kt-doctor-info .kt-doctor-name,
                .kt-doctorPage .kt-body .kt-panel-column h3,
                .kt-doctorPage .kt-body .kt-opening-hours .kt-day-hours .kt-day-name,
                .kt-doctorPage .kt-body .kt-address-section .kt-transport-info h3 {
                    color: ${primaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-panel h2::before {
                    background: linear-gradient(135deg, ${secondaryColor} 0%, ${secondaryColor} 100%);
                }
                
                .kt-doctorPage .kt-body .kt-profile-top .kt-profile-info .kt-specialty,
                .kt-doctorPage .kt-body .kt-panel a,
                .kt-doctorPage .kt-body .kt-loading .kt-loading-spinner,
                .kt-doctorPage .kt-body .kt-btn,                
                .kt-doctorPage .kt-body .kt-contact-info .kt-contact-item i,
                .kt-doctorPage .kt-body .kt-address-section .kt-transport-item i,
                .kt-doctorPage .kt-body .kt-languages i,
                .kt-doctorPage .kt-body .kt-website i {
                    color: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-btn {
                    background: linear-gradient(135deg, ${secondaryColor} 0%, ${secondaryColor} 100%);
                }
                
                .kt-doctorPage .kt-body .kt-btn.kt-btn-outline,
                .kt-doctorPage .kt-body .kt-btn.kt-btn-outline button {
                    color: ${secondaryColor};
                    border-color: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-btn.kt-btn-outline:hover,
                .kt-doctorPage .kt-body .kt-btn.kt-btn-outline:hover i,
                .kt-doctorPage .kt-body .kt-btn.kt-btn-outline:hover a {
                    background: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-btn.kt-btn-outline i {
                    color: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-opening-hours .kt-expand-hours button,
                .kt-doctorPage .kt-body .kt-opening-hours .kt-collapse-hours button {
                    color: ${secondaryColor};
                    border-color: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-opening-hours .kt-expand-hours button:hover,
                .kt-doctorPage .kt-body .kt-opening-hours .kt-collapse-hours button:hover {
                    background: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-profile-top .kt-profile-info h1 {
                    color: ${primaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-loading h2 {
                    color: ${primaryColor};
                }
                
                /* Additional overrides for better coverage */
                .kt-doctorPage .kt-body .kt-two-small-columns .kt-arrow-down i {
                    background: ${secondaryColor};
                }
                
                .kt-doctorPage .kt-body .kt-two-small-columns .kt-arrow-down i:hover {
                    background: ${secondaryColor};
                }

                .kt-doctorPage .kt-body .kt-opening-hours .kt-today-hours {
                    color: white;
                    background: ${secondaryColor};
                }

                html body div#root div.App div.kt-doctorPage div.kt-body div.kt-bookingPanel.noselect div.kt-btn-big.kt-btn-secondary-color {
                    background: ${secondaryColor};
                }

            `;
            document.head.appendChild(style);
            
            console.log('Custom colors applied successfully:', {
                primary: primaryColor,
                secondary: secondaryColor,
                timestamp: new Date().toISOString(),
                locationId: locationData.id || 'unknown'
            });
        }
    };

    /**
     * Note: The long "recursivelyTraverse..." entries in console logs are React's internal
     * development mode effects that intentionally run twice to detect side effects.
     * This is normal in development and won't happen in production.
     */

    /**
     * Resets all custom colors back to the default CSS values
     */
    const resetColors = () => {
        try {
            // Don't reset colors if we're in the middle of navigation
            if (isNavigating) {
                console.log('Skipping color reset during navigation');
                return;
            }
            
            // Don't reset colors if component is unmounted
            if (!isMountedRef.current) {
                console.log('Skipping color reset - component unmounted');
                return;
            }
            
            console.log('Resetting colors to default', new Error().stack);
            
            const root = document.documentElement;
            root.style.removeProperty('--kt-primary-color');
            root.style.removeProperty('--kt-primary-color-light');
            root.style.removeProperty('--kt-secondary-color');
            root.style.removeProperty('--kt-secondary-color-light');
            
            // Remove any custom style elements
            const customStyles = document.querySelectorAll('style[data-custom-colors]');
            customStyles.forEach(style => style.remove());
            
            console.log('Colors reset to default');
        } catch (error) {
            console.error('Error resetting colors:', error);
        }
    };

    /**
     * Updates custom colors when location data changes, with error handling
     * @param locationData - The location data containing custom color configuration
     */
    const updateCustomColors = (locationData: CustomColorConfig) => {
        try {
            console.log('Updating custom colors for location:', locationData.id || 'unknown');
            
            // Only reset if we're actually changing colors (avoid unnecessary resets)
            const currentStyle = document.querySelector('style[data-custom-colors]');
            if (currentStyle) {
                // Remove existing custom styles
                currentStyle.remove();
            }
            
            // Apply new custom colors if they exist
            if (locationData) {
                applyCustomColors(locationData);
            }
        } catch (error) {
            console.error('Error updating custom colors:', error);
            // Fallback to default colors
            resetColors();
        }
    };


    useEffect(() => {
        setIsLoading(true);

        onAuthStateChanged(getAuth(), async function (user) {
            if (user) {
                // User is signed in.
                setAuthUser(user);

                const result = await PatientsService.getPatient(user.uid)

                setPatient(result ?? null);

            } else {
                // User is signed out.
                setAuthUser(null);
                setPatient(null);
            }
        });


    }, []);

    useEffect(() => {
        scrollToTop();
        setShowOnlyTodayOpeningHours(true);
        
        // Set navigation flag to prevent premature color resets
        setIsNavigating(true);
        
        // Don't reset colors here - let the new location data handle it
    }, [clientId, locationId, userId]);

    async function loadAllData() {

        console.log(`loadAllData: clientId: ${clientId} locationId: ${locationId}`);

        if (clientId && locationId) {
            console.log("loadAllData: Both clientId and locationId are available, proceeding...");

            try {
                console.log("loadAllData: getLocation");
                const locationData = await ClientLocationsService.getLocation(clientId, locationId)
                console.log("locationData: " + locationData);
                
                if (locationData) {
                    setLocation(locationData);
                    setLocationLogoUrl(locationData.logoUrl);
                    //updateCustomColors(locationData); // Apply custom colors
                } else {
                    console.warn("loadAllData: Location data is null, this might indicate an issue");
                }

                const clientData = await ClientsService.getClient(clientId)

                setClient(clientData);

                const usersData = await UsersService.getUsersByLocationId(clientId, locationId);

                if (usersData) {

                    const calendarsData = await CalendarsService.getCalendars(clientId, locationId, true);

                    const onlineUsers: any[] = [];

                    for (let i = 0; i < usersData.length; i++) {
                        const doctor = usersData[i];
                        console.log("doc id: " + calendarsData![0].userId);
                        if (calendarsData?.some(c => c.userId === doctor.id)) {
                            onlineUsers.push(doctor);
                        }
                    }

                    // Sort onlineUsers by cardinality before setting
                    onlineUsers.sort((a, b) => (a.cardinality || 0) - (b.cardinality || 0));

                    setDoctors(onlineUsers);
                }

            } catch (error) {
                console.error("loadAllData: Error occurred:", (error as any).message);
                console.error("loadAllData: Full error:", error);
                
                // On mobile, sometimes Firebase calls can fail due to network issues
                // Log additional context for debugging
                console.log("loadAllData: Current state - clientId:", clientId, "locationId:", locationId);
                
                // On mobile, retry once after a short delay
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                if (isMobile) {
                    console.log("loadAllData: Mobile device detected, retrying after 500ms...");
                    setTimeout(async () => {
                        try {
                            console.log("loadAllData: Retry attempt...");
                            const retryLocationData = await ClientLocationsService.getLocation(clientId, locationId);
                            if (retryLocationData) {
                                console.log("loadAllData: Retry successful, location data:", retryLocationData);
                                setLocation(retryLocationData);
                                setLocationLogoUrl(retryLocationData.logoUrl);
                            }
                        } catch (retryError) {
                            console.error("loadAllData: Retry failed:", retryError);
                        }
                    }, 500);
                }
            }

            setIsLoading(false);
        } else {
            console.log(`loadAllData: Missing required parameters - clientId: ${clientId}, locationId: ${locationId}`);
        }
    }

    useEffect(() => {

        // On mobile, add a small delay to ensure all parameters are properly loaded
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        if (isMobile && clientId && locationId) {
            console.log("doctorPage.useEffect: Mobile device detected, adding small delay");
            setTimeout(() => {
                loadAllData();
            }, 100);
        } else {
            loadAllData();
        }

        console.log("doctorPage.useEffect");
        
        // Only reset colors when component unmounts completely, not when clientId changes

    }, [clientId, locationId]);

    // Effect to update colors when location changes
    useEffect(() => {
        console.log('Location changed:', location?.id || 'unknown');
        if (location) {
            updateCustomColors(location);
            // Clear navigation flag after colors are applied
            setIsNavigating(false);
        }
    }, [location]);

    // Cleanup effect that only runs when component unmounts completely
    useEffect(() => {
        return () => {
            // Mark component as unmounted
            isMountedRef.current = false;
            // Only reset colors when the entire component unmounts
            resetColors();
        };
    }, []);

    useEffect(() => {

        async function setAvatarAndUserDetails() {
            try {

                let user: any = null;

                if (userId && clientId) {
                    // get the user details
                    user = await UsersService.getUser(clientId, userId);
                }

                setDoctor(user);

                if (clientId && locationId) {

                    const lastVisitId = user?.id ?? "profile";
                    const lastVisitInDays = LoggingService.getLastVisitInDays(lastVisitId);

                    if (lastVisitInDays !== 0) {
                        const patientName = (patient && patient.lastName) ? `${patient.firstName} ${patient.lastName} hat ` : "";
                        let doctorName = (user && user.lastName) ? ` von ${user.title} ${user.lastName}` : "";

                        const message = `${patientName}Praxisprofil${doctorName} aufgerufen`;

                        LoggingService.log(LogItemType.doctorProfileLoaded, "", patient?.id, user?.id, message, clientId, locationId);
                    }

                    LoggingService.storeLastVisitTimestamp(lastVisitId);
                }

            } catch (error) {
                console.error((error as any).message);
            }
        }

        setAvatarAndUserDetails();
    }, [userId, clientId]);



    function getLanguagesString() {
        let langString = "";
        let languages = doctor ? doctor.languages : location?.languages;

        if (languages) {
            let langCount = languages.length;
            for (let i = 0; i < langCount; i++) {
                const language = languages[i];

                if (i === 0) {
                    langString = language;
                } else if (i === langCount - 1 && langCount > 1) {
                    langString += ` ${t("pages.doctorPage.and")} ${language}`;
                } else {
                    langString += `, ${language}`;
                }
            }
        }

        return langString;
    }

    function getOpeningHoursString(workingDay) {
        if (workingDay && workingDay.open && workingDay.open.start && workingDay.open.end) {

            if (!workingDay.hasOpen) {
                return t("pages.doctorPage.closed");

            } else if (workingDay.hasPause && workingDay.pause && workingDay.pause.start && workingDay.pause.end) {
                // Handle pause/break in opening hours
                const morningStart = `${getWithLeadingZero(workingDay.open.start.hour)}:${getWithLeadingZero(workingDay.open.start.minute)}`;
                const morningEnd = `${getWithLeadingZero(workingDay.pause.start.hour)}:${getWithLeadingZero(workingDay.pause.start.minute)}`;
                const afternoonStart = `${getWithLeadingZero(workingDay.pause.end.hour)}:${getWithLeadingZero(workingDay.pause.end.minute)}`;
                const afternoonEnd = `${getWithLeadingZero(workingDay.open.end.hour)}:${getWithLeadingZero(workingDay.open.end.minute)}`;

                return (
                    <div className="kt-time-ranges">
                        <div className="kt-time-range">{morningStart} - {morningEnd}</div>
                        <div className="kt-time-range">{afternoonStart} - {afternoonEnd}</div>
                    </div>
                );

            } else {
                // Single time range
                const start = `${getWithLeadingZero(workingDay.open.start.hour)}:${getWithLeadingZero(workingDay.open.start.minute)}`;
                const end = `${getWithLeadingZero(workingDay.open.end.hour)}:${getWithLeadingZero(workingDay.open.end.minute)}`;
                return `${start} - ${end}`;
            }

        } else {
            return "";
        }
    }

    function getOpeningHours() {

        if (location) {


            const mom = moment();
            const allHours = (doctor && doctor.openingHours && doctor.openingHours.enabled) ? doctor.openingHours : location.openingHours;
            const todayIndex = mom.day();

            const weekdays = moment.weekdays();

            if (allHours && allHours.monday) {
                if (showOnlyTodayOpeningHours) {
                    const todayHours = getOpeningHoursByDayIndex(allHours, todayIndex);
                    return (
                        <div className="kt-opening-hours">
                            <div className="kt-today-hours">
                                <span>{t("pages.doctorPage.today")}</span>
                                <span>{getOpeningHoursString(todayHours)}</span>
                            </div>
                            <div className="kt-expand-hours">
                                <button 
                                    className="kt-btn kt-btn-outline" 
                                    onClick={() => setShowOnlyTodayOpeningHours(false)}
                                >
                                    <i className="fas fa-chevron-down" aria-hidden="true"></i>
                                    {t("pages.doctorPage.showAllDays")}
                                </button>
                            </div>
                        </div>
                    );
                } else {
                    let result: any[] = [];
                    for (let i = 0; i < 7; i++) {
                        const day = weekdays[i];
                        const dayHours = getOpeningHoursByDayIndex(allHours, i);
                        if (dayHours) {
                            result.push(
                                <div key={i} className="kt-day-hours">
                                    <span className="kt-day-name">{day}:</span>
                                    <span className="kt-day-time">{getOpeningHoursString(dayHours)}</span>
                                </div>
                            );
                        }
                    }

                    return (
                        <div className="kt-opening-hours">
                            {result}
                            <div className="kt-collapse-hours">
                                <button 
                                    className="kt-btn kt-btn-outline" 
                                    onClick={() => setShowOnlyTodayOpeningHours(true)}
                                >
                                    <i className="fas fa-chevron-up" aria-hidden="true"></i>
                                    {t("pages.doctorPage.showTodayOnly")}
                                </button>
                            </div>
                        </div>
                    );
                }
            }
        }

        return "";
    }

    function getTeamMembers() {
        if (doctors && doctors.length > 0) {
            return (
                <div className="kt-team-grid">
                    {doctors.map((user) =>
                        <Link to={`/profile/${clientId}/${locationId}/${user.id}`} key={user.id} className="kt-avatar-columns">
                            <Avatar src={user.avatarUrl} alt="" width="60px" />
                            <div className="kt-doctor-info">
                                <div className="kt-doctor-name">{user.title} {user.firstName} {user.lastName}</div>
                                {/* <div className="kt-doctor-specialty">{user.specialty || t("pages.doctorPage.generalPractitioner")}</div> */}
                            </div>
                        </Link>
                    )}
                </div>
            );
        } else {
            return <div className="kt-no-team">{t("pages.doctorPage.noTeamMembers")}</div>;
        }
    }

    if (isLoading) {
        return (
            <div className="kt-doctorPage">
                <div className="kt-body">
                    <div className="kt-panel kt-loading">
                        <div className="kt-panel-content">
                            <div className="kt-loading-content">
                                <div className="kt-loading-spinner">
                                    <i className="fas fa-circle-notch fa-spin"></i>
                                </div>
                                <h2>{t("pages.doctorPage.loading")}</h2>
                                <p>{t("pages.doctorPage.loadingDescription")}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="kt-doctorPage">

            {(!isLoading && (!client || !location || (userId && !doctor))) ? (
                <div className="kt-body">
                    <div className="kt-panel kt-error">
                        <div className="kt-panel-content">
                            <div className="kt-error-content">
                                <i className="fas fa-exclamation-triangle"></i>
                                <h1>{t("pages.doctorPage.somethingWentWrong")}</h1>
                                <h2>{t("pages.doctorPage.couldNotFindPage")}</h2>
                                <p>{t("pages.doctorPage.pleaseCheckUrl")}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) :

                (
                    <div className="kt-body">

                        <div className="kt-panel kt-hero">
                            <div className="kt-panel-content">
                                {userId ?
                                    <div className="kt-profile-top">
                                        {location.isLogoVisible && <Avatar src={doctor.avatarUrl} alt="" width="160px" />}
                                        <div className="kt-profile-info">
                                            {location.customBookingHeadlineText ?
                                                <h1>{location.customBookingHeadlineText}</h1>
                                                :
                                                <h1>{getUserName(doctor)}</h1>
                                            }
                                            {doctor?.specialty && <p className="kt-specialty">{doctor.specialty}</p>}
                                        </div>
                                    </div>
                                    :
                                    <div className="kt-profile-top">
                                        {location.isLogoVisible && <Avatar src={locationLogoUrl} alt="" width="160px" fitImage={true} noCircle={true} noBorder={true} />}
                                        <div className="kt-profile-info">
                                            {location.customBookingHeadlineText ?
                                                <h1>{location.customBookingHeadlineText}</h1>
                                                :
                                                <h1>{location.name}</h1>
                                            }
                                            {location.customBookingSubHeadlineText && <p className="kt-subtitle">{location.customBookingSubHeadlineText}</p>}
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>

                        <BookingPanel
                            client={client}
                            location={location}
                            doctor={doctor}
                            doctors={doctors}
                        />

                        <div className="kt-panel kt-with-icon">
                            <div className="kt-panel-icon"><i className="far fa-clock" aria-hidden="true"></i></div>
                            <div className="kt-content-header">
                                {location.isOpenHoursVisible ?
                                    <h2>{t("pages.doctorPage.openingHoursAndContact")}</h2>
                                    :
                                    <h2>{t("pages.doctorPage.contact")}</h2>}

                                <div className="kt-panel-2column-row">

                                    {location.isOpenHoursVisible && <div className="kt-panel-column">
                                        <h3>{t("pages.doctorPage.openingHours")}</h3>
                                        {getOpeningHours()}
                                    </div>}

                                    <div className="kt-panel-column">
                                        <h3>{t("pages.doctorPage.phoneNumbers")}</h3>
                                        <div className="kt-contact-info">
                                            <div className="kt-contact-item">
                                                <i className="fas fa-phone"></i>
                                                <span>{t("pages.doctorPage.phoneNumber")} <a href={"tel:" + location.phoneNumber}>{location.phoneNumber}</a></span>
                                            </div>
                                            {location.faxNumber && 
                                                <div className="kt-contact-item">
                                                    <i className="fas fa-fax"></i>
                                                    <span>{t("pages.doctorPage.faxNumber")} <span>{location.faxNumber}</span></span>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                </div>

                                {location.email &&
                                    <div className="kt-panel-column kt-email-section">
                                        <h3>{t("pages.doctorPage.email")}</h3>
                                        <div className="kt-contact-item">
                                            <i className="fas fa-envelope" style={{marginRight: "10px"}}></i>
                                            <a href={`mailto:${location.email}`}>{location.email}</a>
                                        </div>
                                    </div>
                                }

                            </div>
                        </div>

                        {location.isAddressVisible && <div className="kt-panel kt-with-icon">
                            <div className="kt-panel-icon"><i className="fal fa-map-marker-alt" aria-hidden="true"></i></div>
                            <div className="kt-panel-content">
                                <h2>{t("pages.doctorPage.adress")}</h2>
                                <div className="kt-address-section">
                                    <div className="kt-address-content">
                                        <h3><Link to={`/profile/${clientId}/${locationId}`}>{location.name}</Link></h3>
                                        <div className="kt-address-details">
                                            {location.street}<br />
                                            {location.postalCode} {location.city}
                                        </div>
                                        <div className="kt-route-planner">
                                            {location.googleMapsLink &&
                                                <a className="kt-btn kt-btn-outline" href={location.googleMapsLink} target="_blank" rel="noopener noreferrer">
                                                    <i className="fal fa-map-marker-alt" aria-hidden="true"></i>
                                                    <span> {t("pages.doctorPage.routePlanner")}</span>
                                                </a>
                                            }
                                        </div>

                                        {location.publicTransports.length > 0 && 
                                            <div className="kt-transport-info">
                                                <h3>{t("pages.doctorPage.publicTransport")}</h3>
                                                {location.publicTransports.map((transport, index) =>
                                                    <div key={index} className="kt-transport-item">
                                                        <i className="fas fa-bus"></i>
                                                        <span>{transport}</span>
                                                    </div>
                                                )}
                                            </div>
                                        }

                                        {location.accessInformation.length > 0 && 
                                            <div className="kt-transport-info">
                                                <h3>{t("pages.doctorPage.accessInformation")}</h3>
                                                {location.accessInformation.map((accessInfo, index) =>
                                                    <div key={index} className="kt-transport-item">
                                                        <i className="fas fa-wheelchair"></i>
                                                        <span>{accessInfo}</span>
                                                    </div>
                                                )}
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>}


                        {!userId ?
                            <div className="kt-panel kt-with-icon kt-team-section">
                                <div className="kt-panel-icon"><i className="fa fa-user-md" aria-hidden="true"></i></div>
                                <div className="kt-panel-content">
                                    <h2>{t("pages.doctorPage.ourTeam")}</h2>
                                    {getTeamMembers()}
                                </div>
                            </div>
                            : ""
                        }

                        <div className="kt-panel kt-with-icon">
                            <div className="kt-panel-icon"><i className="far fa-id-card" aria-hidden="true"></i></div>
                            <div className="kt-panel-content">

                                <h2>{doctor ? t("pages.doctorPage.doctorProfile") : t("pages.doctorPage.profile")}</h2>
                                <div className="kt-profile-content">
                                    {doctor && doctor.profile ?
                                        <div className='display-linebreak'>{doctor.profile}</div>
                                        :
                                        <div className='display-linebreak'>{location.profile}</div>
                                    }
                                </div>
                                <div className="kt-panel-row">
                                    {getLanguagesString() &&
                                        <div className="kt-panel-column">
                                            <h3>{t("pages.doctorPage.languages")}</h3>
                                            <div className="kt-languages">
                                                <i className="fas fa-language"></i>
                                                <span>{getLanguagesString()}</span>
                                            </div>
                                        </div>
                                    }

                                    {location.homepage &&
                                        <div className="kt-panel-column">
                                            <h3>{t("pages.doctorPage.website")}</h3>
                                            <div className="kt-website">
                                                <i className="fas fa-globe"></i>
                                                <a href={location.homepage} target="_blank" rel="noopener noreferrer" >{location.homepage.replace("https://", "").replace("http://", "")}</a>
                                            </div>
                                        </div>
                                    }

                                </div>

                            </div>
                        </div>


                    </div>
                )}


        </div>
    );
}